import { useState } from 'react';
import { useOutages } from '../../hooks/useOutages';
import { useGridStatus, type GridStatus } from '../../hooks/useGridStatus';
import { useWeather } from '../../hooks/useWeather';
import { usePeakLoad } from '../../hooks/usePeakLoad';
import { useEnergy } from '../../hooks/useEnergy';
import { StatValue } from '../../components/atoms/StatValue/StatValue';
import { Card } from '../../components/atoms/Card/Card';
import { ErrorMessage } from '../../components/atoms/ErrorMessage/ErrorMessage';
import { LoadingSkeleton } from '../../components/atoms/LoadingSkeleton/LoadingSkeleton';
import { LegendColorBox } from '../../components/atoms/LegendColorBox/LegendColorBox';
import { BottomSheet } from '../../components/molecules/BottomSheet/BottomSheet';
import { CommunityMap } from '../map/CommunityMap';
import { useChartInteraction } from '../../contexts/ChartInteractionContext';
import { COMMUNITY_COLORS } from '../../constants';
import { StormBanner } from './StormBanner';
import './CurrentOutages.css';
import '../weather/CurrentWeather.css';

export function CurrentOutages() {
  const { data: outageData, isLoading, error } = useOutages();
  const { data: weatherData } = useWeather();
  const { hoveredCommunity, setHoveredCommunity } = useChartInteraction();
  const [showGridStatusInfo, setShowGridStatusInfo] = useState(false);
  const { data: gridStatusData } = useGridStatus();
  const { data: peakLoadData } = usePeakLoad();
  const { data: energyData } = useEnergy();

  if (isLoading) {
    return <LoadingSkeleton variant="text" />;
  }

  if (error) {
    return <ErrorMessage message={error.message} />;
  }

  if (!outageData) {
    return <p>No outage data available</p>;
  }

  const getTotalCustomersOut = () => {
    return outageData.communities.reduce((total, community) => {
      return total + (community.customersOut || 0);
    }, 0);
  };

  const totalCustomersOut = getTotalCustomersOut();
  const stormActive = !!outageData.activeOutageEvent;
  const affectedCount = outageData.communities.filter(
    (c) => (c.customersOut || 0) > 0,
  ).length;

  const mapSection = (
    <div className="CurrentOutages-mapSection">
      <Card>
        <h3>Island Overview</h3>
        <CommunityMap />
      </Card>
    </div>
  );

  return (
    <>
      {stormActive && (
        <>
          <StormBanner
            event={outageData.activeOutageEvent!}
            totalCustomersOut={totalCustomersOut}
            communitiesAffected={affectedCount}
          />
          {mapSection}
        </>
      )}
      <div className="CurrentOutages-grid">
        <Card>
          <StatValue
            value={totalCustomersOut}
            label="Customers Without Power"
            variant={totalCustomersOut === 0 ? 'ok' : 'alert'}
          />
          <div className="CurrentOutages-outageStatus">
            {totalCustomersOut > 0 ? (
              <>
                <span className="CurrentOutages-outageStatusLabel CurrentOutages-outageStatusLabel--active">
                  Active Outage
                </span>
                {outageData.outageStartedAt && (
                  <span className="CurrentOutages-outageStatusTime">
                    Since {new Date(outageData.outageStartedAt).toLocaleString('en-US', {
                      timeZone: 'America/Halifax',
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </span>
                )}
              </>
            ) : (
              <span className="CurrentOutages-outageStatusLabel CurrentOutages-outageStatusLabel--ok">
                No Active Outages
              </span>
            )}
          </div>
        </Card>

        <Card className="CurrentOutages-gridStatusCard">
          {(() => {
            const status = gridStatusData?.status ?? 'normal';
            const alertMessages = gridStatusData?.alertMessages ?? [];

            // Grid status card based on Maritime Electric status
            if (status === 'load_shedding') {
              return (
                <>
                  <div className="CurrentOutages-iconLoadShedding">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
                      <line x1="12" y1="2" x2="12" y2="12" />
                    </svg>
                  </div>
                  <div className="CurrentOutages-title CurrentOutages-loadShedding">Load Shedding</div>
                  <div className="CurrentOutages-subtitle">Rolling blackouts in progress</div>
                  {alertMessages.length > 0 && (
                    <div className="CurrentOutages-alertMessages">
                      {alertMessages.map((msg, i) => (
                        <div key={i} className="CurrentOutages-alertMessage">{msg}</div>
                      ))}
                    </div>
                  )}
                </>
              );
            }

            if (status === 'warning') {
              return (
                <>
                  <div className="CurrentOutages-iconWarning" style={{ color: '#f85149' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                  </div>
                  <div className="CurrentOutages-title" style={{ color: '#f85149' }}>Grid Warning</div>
                  <div className="CurrentOutages-subtitle">System approaching max capacity</div>
                  {alertMessages.length > 0 && (
                    <div className="CurrentOutages-alertMessages">
                      {alertMessages.map((msg, i) => (
                        <div key={i} className="CurrentOutages-alertMessage">{msg}</div>
                      ))}
                    </div>
                  )}
                </>
              );
            }

            if (status === 'watch') {
              return (
                <>
                  <div className="CurrentOutages-iconWatch" style={{ color: '#d29922' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                    </svg>
                  </div>
                  <div className="CurrentOutages-title" style={{ color: '#d29922' }}>Grid Watch</div>
                  <div className="CurrentOutages-subtitle">Higher demand expected</div>
                  {alertMessages.length > 0 && (
                    <div className="CurrentOutages-alertMessages">
                      {alertMessages.map((msg, i) => (
                        <div key={i} className="CurrentOutages-alertMessage">{msg}</div>
                      ))}
                    </div>
                  )}
                </>
              );
            }

            // Normal grid status - show "All Clear"
            return (
              <>
                <div className="CurrentOutages-iconOk">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <div className="CurrentOutages-title">All Clear</div>
                <div className="CurrentOutages-subtitle">Grid operating normally</div>
              </>
            );
          })()}
          <button
            className="CurrentOutages-infoButton"
            onClick={() => setShowGridStatusInfo(true)}
            aria-label="More info about grid status"
          >
            i
          </button>
        </Card>

        {weatherData?.peiAverage && (
          <Card className="CurrentWeather-card">
            <div className="CurrentWeather-stats">
              <div className="CurrentWeather-stat">
                <span className="CurrentWeather-value">
                  {weatherData.peiAverage.temperature}°C
                </span>
                <span className="CurrentWeather-label">Avg. Temperature</span>
              </div>
              <div className="CurrentWeather-stat">
                <span className="CurrentWeather-value">
                  {weatherData.peiAverage.windSpeed} km/h
                </span>
                <span className="CurrentWeather-label">Avg. Wind Speed</span>
              </div>
            </div>
          </Card>
        )}

        {weatherData?.worstConditions && (
          <Card className="CurrentWeather-card">
            <div className="CurrentWeather-stats">
              <div className="CurrentWeather-stat">
                <span className="CurrentWeather-value">
                  {weatherData.worstConditions.lowestTemp}°C
                </span>
                <span className="CurrentWeather-label">
                  Coldest ({weatherData.worstConditions.coldestCity})
                </span>
              </div>
              <div className="CurrentWeather-stat">
                <span className="CurrentWeather-value">
                  {weatherData.worstConditions.highestWind} km/h
                </span>
                <span className="CurrentWeather-label">
                  Strongest Wind ({weatherData.worstConditions.windiestCity})
                </span>
              </div>
            </div>
          </Card>
        )}

        {peakLoadData && (() => {
          const currentLoad = energyData?.find(g => g.gaugeType === 'Total On-Island Load')?.value ?? 0;
          const isAtPeak = currentLoad >= peakLoadData.value - 1;
          const start = new Date(peakLoadData.startTime);

          const getRelativeDate = (): string => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const dateOnly = new Date(start);
            dateOnly.setHours(0, 0, 0, 0);
            const diffDays = Math.floor((today.getTime() - dateOnly.getTime()) / (1000 * 60 * 60 * 24));
            if (diffDays === 0) return 'Today';
            if (diffDays === 1) return 'Yesterday';
            if (diffDays < 7) return `${diffDays} days ago`;
            return start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          };

          return (
            <Card className="CurrentWeather-card">
              <div className="CurrentWeather-stats">
                <div className="CurrentWeather-stat">
                  <span className="CurrentWeather-value" style={{ color: isAtPeak ? '#f85149' : undefined }}>
                    {Math.round(peakLoadData.value)} MW
                  </span>
                  <span className="CurrentWeather-label">
                    {isAtPeak ? 'Peak Load - Right Now!' : 'Peak Load'}
                  </span>
                </div>
                <div className="CurrentWeather-stat">
                  <span className="CurrentWeather-value">
                    {isAtPeak ? 'Now' : getRelativeDate()}
                  </span>
                  <span className="CurrentWeather-label">Recorded</span>
                </div>
              </div>
            </Card>
          );
        })()}
      </div>

      {!stormActive && mapSection}

      {outageData.communities.length > 0 && (
        <div className="CurrentOutages-communitiesSection">
          <Card>
            <h3>Affected Communities</h3>
            <ul className="CurrentOutages-communitiesList">
              {outageData.communities.map((c, i) => (
                <li
                  key={i}
                  className={
                    hoveredCommunity === c.name ? 'CurrentOutages-highlighted' : ''
                  }
                  onMouseEnter={() => setHoveredCommunity(c.name)}
                  onMouseLeave={() => setHoveredCommunity(null)}
                >
                  <div className="CurrentOutages-communityLegendItem">
                    <LegendColorBox
                      color={COMMUNITY_COLORS[i % COMMUNITY_COLORS.length]}
                    />
                    <div className="CurrentOutages-communityInfo">
                      <span className="CurrentOutages-communityName">{c.name}</span>
                      <span className="CurrentOutages-county">{c.county} County</span>
                    </div>
                    {c.customersOut !== undefined && (
                      <span className="CurrentOutages-customerCount">
                        {c.customersOut}{' '}
                        {c.customersOut > 1 ? 'customers' : 'customer'}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      )}

      <BottomSheet
        isOpen={showGridStatusInfo}
        onClose={() => setShowGridStatusInfo(false)}
        title="Grid Status"
      >
        <div className="CurrentOutages-sheetContent">
          <div className="CurrentOutages-sheetSection">
            <strong>What is Grid Status?</strong>
            <p>
              Maritime Electric publishes a Grid Status Index to indicate how close
              the power system is to exceeding capacity. When demand approaches
              available supply, they issue alerts to help Islanders prepare.
            </p>
          </div>
          <div className="CurrentOutages-sheetSection">
            <strong>Status Levels:</strong>
            <p>
              <strong style={{ color: '#3fb950' }}>Normal:</strong> Power system operating under normal conditions.
            </p>
            <p>
              <strong style={{ color: '#d29922' }}>Watch:</strong> Higher than usual demand expected within 72 hours. Be prepared to conserve energy if asked.
            </p>
            <p>
              <strong style={{ color: '#f85149' }}>Warning:</strong> System forecast to approach maximum capacity within 24 hours. Reduce usage and prepare for possible rotating outages.
            </p>
            <p>
              <strong style={{ color: '#da3633' }}>Load Shedding:</strong> Controlled rotating outages are in effect to protect the power system.
            </p>
          </div>
          <div className="CurrentOutages-sheetSection">
            <strong>Source:</strong>
            <a
              href="https://www.maritimeelectric.com/outages/outages/grid-status/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Maritime Electric - Grid Status
            </a>
          </div>
        </div>
      </BottomSheet>
    </>
  );
}
